import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Time to allow the pointer to travel across the gap between the dot and its label before the
 * label closes. Mirrors the mega menu's DEACTIVATE_DELAY in header-menu.js.
 */
const CLOSE_DELAY = 300;

/**
 * A single hotspot on a `hotspot-image` section. Reveals its label on hover, on keyboard focus
 * and on tap; the label itself carries the link.
 *
 * Hover alone is not enough (touch devices have none) and CSS `:focus-within` alone is not
 * enough either (Safari does not reliably focus a `<button>` on click), so the open state is
 * held in `aria-expanded` on the trigger and driven from here.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The hotspot dot.
 * @property {HTMLElement} label - The label revealed on hover/focus/tap.
 *
 * @extends {Component<Refs>}
 */
class HotspotComponent extends Component {
  requiredRefs = ['trigger', 'label'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode) {
      const { signal } = this.#abortController;

      // Reveal the label while the merchant has this hotspot selected, so the label position
      // and text can be tuned without hovering. The theme editor dispatches these on the block.
      this.addEventListener('shopify:block:select', this.#openNow, { signal });
      this.addEventListener('shopify:block:deselect', this.#closeNow, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#debouncedClose.cancel();
    this.#abortController.abort();
  }

  /**
   * Whether the label is currently open.
   * @returns {boolean}
   */
  get expanded() {
    return this.refs.trigger.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Open the label immediately, closing any other open hotspot so taps cannot stack labels.
   */
  open = () => {
    this.#debouncedClose.cancel();

    for (const other of document.querySelectorAll('hotspot-component')) {
      if (other !== this && other instanceof HotspotComponent && other.expanded) {
        other.closeImmediately();
      }
    }

    this.#openNow();
  };

  /**
   * Close the label after a delay, unless the pointer has moved onto the label itself.
   */
  close = () => {
    this.#debouncedClose();
  };

  /**
   * Toggle the label. Used for taps, where there is no hover to rely on.
   */
  toggle = () => {
    if (this.expanded) {
      this.closeImmediately();
    } else {
      this.open();
    }
  };

  /**
   * Close the label right away, skipping the debounce and the hover check.
   */
  closeImmediately = () => {
    this.#debouncedClose.cancel();
    this.#closeNow();
  };

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.expanded) {
      event.stopPropagation();
      this.closeImmediately();
    }
  };

  #openNow = () => {
    this.refs.trigger.setAttribute('aria-expanded', 'true');
  };

  #closeNow = () => {
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  };

  /**
   * Hover bridge: the label sits 8px away from the dot, so crossing that gap briefly leaves
   * both. Bail out if the pointer landed on the label. Same trick as header-menu.js.
   */
  #closeHandler = () => {
    if (this.refs.label?.matches(':hover')) return;
    if (this.contains(document.activeElement)) return;

    this.#closeNow();
  };

  #debouncedClose = debounce(this.#closeHandler, CLOSE_DELAY);
}

if (!customElements.get('hotspot-component')) {
  customElements.define('hotspot-component', HotspotComponent);
}

/**
 * Derives a pairing key from a link: the last segment of its path.
 *
 * A store's card and its hotspot both link to the same landing page, so both hrefs end in the
 * store's handle - `/pages/stores/muenchen-laim` and the card's booking link
 * `https://rebike.com/pages/stores/muenchen-laim#calendly` alike. That handle is the key, which
 * means pairing needs no configuration by the merchant.
 *
 * @param {string | null} href
 * @returns {string | null}
 */
function pairingKey(href) {
  if (!href) return null;

  try {
    const path = new URL(href, window.location.href).pathname.replace(/\/+$/, '');
    return path.split('/').pop() || null;
  } catch {
    return null;
  }
}

/**
 * The image frame. Optionally pairs each hotspot with a matching card elsewhere on the page, so
 * highlighting one highlights the other.
 *
 * Pairing is driven off `aria-expanded` on the hotspot triggers rather than its own pointer
 * handlers, so it inherits hover, keyboard focus and tap behaviour from HotspotComponent for
 * free instead of duplicating that logic.
 *
 * @extends {Component<{}>}
 */
class HotspotImageComponent extends Component {
  /** @type {Map<HTMLElement, HTMLElement>} hotspot -> paired card */
  #cards = new Map();

  #observer = new MutationObserver((records) => {
    for (const record of records) {
      const trigger = record.target;
      if (!(trigger instanceof HTMLElement)) continue;

      const hotspot = trigger.closest('hotspot-component');
      const card = hotspot instanceof HTMLElement ? this.#cards.get(hotspot) : null;
      if (!card) continue;

      card.classList.toggle('hotspot-card--active', trigger.getAttribute('aria-expanded') === 'true');
    }
  });

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.linkCards !== 'true') return;

    this.#pairCards();

    if (this.#cards.size === 0) return;

    this.#observer.observe(this, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });

    // The reverse direction: pointing at a card opens its hotspot.
    const { signal } = this.#abortController;
    for (const [hotspot, card] of this.#cards) {
      const open = () => /** @type {any} */ (hotspot).open?.();
      const close = () => /** @type {any} */ (hotspot).close?.();

      card.addEventListener('pointerenter', open, { signal });
      card.addEventListener('pointerleave', close, { signal });
      card.addEventListener('focusin', open, { signal });
      card.addEventListener('focusout', close, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#abortController.abort();
    this.#cards.clear();
  }

  updatedCallback() {
    super.updatedCallback?.();
    // Re-pair after a theme-editor re-render, which replaces the hotspot elements.
    this.#observer.disconnect();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#cards.clear();
    this.connectedCallback();
  }

  #pairCards() {
    /** @type {Map<string, HTMLElement>} */
    const byKey = new Map();

    for (const hotspot of this.querySelectorAll('hotspot-component')) {
      const link = hotspot.querySelector('a[href]');
      const key = pairingKey(link?.getAttribute('href') ?? null);
      if (key && !byKey.has(key)) byKey.set(key, /** @type {HTMLElement} */ (hotspot));
    }

    if (byKey.size === 0) return;

    // Search only the section the map lives in. Site navigation links to every store several
    // times over - header menu, mega menu, mobile drawer - and those links are indistinguishable
    // from a card's by URL alone, so a document-wide search pairs hotspots with menu items.
    const scope = this.closest('.shopify-section') ?? document;

    /** @type {Map<string, Element[]>} every in-scope, off-map link pointing at each store */
    const anchorsByKey = new Map();

    for (const anchor of scope.querySelectorAll('a[href]')) {
      if (this.contains(anchor)) continue;

      const key = pairingKey(anchor.getAttribute('href'));
      if (!key || !byKey.has(key)) continue;

      const list = anchorsByKey.get(key);
      if (list) list.push(anchor);
      else anchorsByKey.set(key, [anchor]);
    }

    for (const [key, hotspot] of byKey) {
      const anchors = anchorsByKey.get(key);
      if (!anchors) continue;

      const name = hotspot.querySelector('.hotspot__label-name')?.textContent?.trim() ?? '';
      const card = findCard(anchors, name, new Set(byKey.keys()));

      if (card) {
        card.classList.add('hotspot-card');
        this.#cards.set(hotspot, card);
      }
    }
  }
}

/**
 * How many distinct stores are linked from inside an element.
 *
 * @param {Element} element
 * @param {Set<string>} keys
 * @returns {number}
 */
function countStores(element, keys) {
  /** @type {Set<string>} */
  const found = new Set();

  for (const link of element.querySelectorAll('a[href]')) {
    const key = pairingKey(link.getAttribute('href'));
    if (key && keys.has(key)) found.add(key);
  }

  return found.size;
}

/**
 * Resolves the element that represents a store's card.
 *
 * Climbs independently from each link pointing at the store until it reaches something that
 * looks like a card, then keeps the smallest result.
 *
 * "Looks like a card" means it carries a heading. That is what separates a card from the two
 * things either side of it: the button wrapper below it has no heading, and the row above it has
 * several. It is also the rule that keeps a link list from qualifying, which matters because a
 * store's name and URL appear in navigation menus as well as on its card.
 *
 * A climb is abandoned once the element links to more than one store, so a stray link elsewhere
 * resolves to nothing rather than to some huge container.
 *
 * @param {Element[]} anchors - every in-scope link pointing at this store
 * @param {string} name - the hotspot's title, used to break ties between equally sized cards
 * @param {Set<string>} keys - every store on this map
 * @returns {HTMLElement | null}
 */
function findCard(anchors, name, keys) {
  /** @type {HTMLElement[]} */
  const candidates = [];

  for (const anchor of anchors) {
    let element = anchor.parentElement;

    while (element && element !== document.body) {
      if (countStores(element, keys) > 1) break;

      if (element.querySelector('h1, h2, h3, h4, h5, h6')) {
        candidates.push(element);
        break;
      }

      element = element.parentElement;
    }
  }

  if (candidates.length === 0) return null;

  // Prefer one naming this store, then the smallest - reduce() over unrelated elements would
  // otherwise just keep whichever came first.
  const named = name ? candidates.filter((c) => c.textContent?.includes(name)) : [];
  const pool = named.length > 0 ? named : candidates;

  return pool.reduce((a, b) => (b.getElementsByTagName('*').length < a.getElementsByTagName('*').length ? b : a));
}

if (!customElements.get('hotspot-image-component')) {
  customElements.define('hotspot-image-component', HotspotImageComponent);
}
